import React, { useState, useMemo, useEffect } from "react";
import {
  Box,
  Typography,
  Autocomplete,
  TextField,
  Chip,
  Button,
  Card,
  CardContent,
  IconButton,
  Tooltip
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import { useBrandsQuery } from "../ProductTypes/hooks";
import { useWebflowBlogs } from "../../../hooks/useWebflow";
import { useContent } from "../../../hooks/useContent";

const DirectReferencesSection = ({ directReferences = [], onChange }) => {
  const [newBrand, setNewBrand] = useState(null);
  const [newArticleSlug, setNewArticleSlug] = useState("");
  const [duplicateWarning, setDuplicateWarning] = useState("");

  // Auto-correct articleSlug if it's an object
  useEffect(() => {
    if (newArticleSlug && typeof newArticleSlug === "object") {
      setNewArticleSlug(newArticleSlug.slug || "");
    }
  }, [newArticleSlug]);

  // Clear duplicate warning when selecting new options
  useEffect(() => {
    setDuplicateWarning("");
  }, [newBrand, newArticleSlug]);

  // Fetch brands and article slugs
  const { data: brandsData } = useBrandsQuery();
  const { data: webflowData } = useWebflowBlogs();
  const { data: contentData } = useContent({ search: "" });

  // Process and merge article slugs from both sources (similar to BrandsByProductType)
  const articleSlugOptions = useMemo(() => {
    const webflowSlugs = webflowData?.items
      ? webflowData.items.map(blog => ({
        slug: blog.slug,
        source: "webflow",
        title: blog.name || blog.title
      }))
      : webflowData?.blogs
        ? webflowData.blogs.map(blog => ({
          slug: blog.slug,
          source: "webflow",
          title: blog.name || blog.title
        }))
        : webflowData
          ? Array.isArray(webflowData)
            ? webflowData.map(blog => ({
              slug: blog.slug,
              source: "webflow",
              title: blog.name || blog.title
            }))
            : []
          : [];

    const contentSlugs = contentData?.content?.map(content => ({
      slug: content.slug,
      source: "content",
      title: content.title
    })) || [];

    const allSlugs = [...webflowSlugs, ...contentSlugs];
    const validSlugs = allSlugs.filter(item => item.slug);

    const uniqueSlugs = [];
    const slugMap = {};

    validSlugs.forEach(item => {
      if (!slugMap[item.slug]) {
        slugMap[item.slug] = { sources: [item.source], title: item.title };
        uniqueSlugs.push(item);
      } else {
        if (!slugMap[item.slug].sources.includes(item.source)) {
          slugMap[item.slug].sources.push(item.source);
        }
      }
    });

    return uniqueSlugs.map(item => ({
      ...item,
      sources: slugMap[item.slug].sources
    }));
  }, [webflowData, contentData]);

  const brands = useMemo(() => brandsData?.brands || [], [brandsData]);

  // Filter out already selected brands and article slugs
  const availableBrands = useMemo(() => {
    const selectedBrandIds = directReferences
      .filter(ref => ref.brandId)
      .map(ref => ref.brandId);

    return brands.filter(brand => !selectedBrandIds.includes(brand.id));
  }, [brands, directReferences]);

  const availableArticleSlugs = useMemo(() => {
    const selectedSlugs = directReferences
      .filter(ref => ref.articleSlug)
      .map(ref => ref.articleSlug);

    return articleSlugOptions.filter(option => !selectedSlugs.includes(option.slug));
  }, [articleSlugOptions, directReferences]);

  const handleAddReference = () => {
    if (!newBrand && !newArticleSlug) { return; }

    // Ensure articleSlug is a string
    const articleSlugValue = typeof newArticleSlug === "object" ? newArticleSlug.slug : newArticleSlug;
    if (newArticleSlug && !articleSlugValue) {
      return;
    }

    // Check if article slug is already selected
    if (newArticleSlug) {
      const isDuplicate = directReferences.some(ref => ref.articleSlug === articleSlugValue);
      if (isDuplicate) {
        setDuplicateWarning(`Article slug "${articleSlugValue}" is already selected`);
        return;
      }
    }

    // Check if brand is already selected
    if (newBrand) {
      const isDuplicate = directReferences.some(ref => ref.brandId === newBrand.id);
      if (isDuplicate) {
        setDuplicateWarning(`Brand "${formatBrandName(newBrand)}" is already selected`);
        return;
      }
    }

    // Clear any previous warnings
    setDuplicateWarning("");

    const newReference = {
      id: `temp-${Date.now()}`, // Temporary ID for new items
      brandId: newBrand ? newBrand.id : null,
      articleSlug: newArticleSlug ? articleSlugValue : null,
      order: directReferences.length
    };

    // Add brand data if it's a brand reference
    if (newBrand) {
      newReference.Brands = newBrand;
    }

    const updatedReferences = [...directReferences, newReference];
    onChange(updatedReferences);

    // Reset form
    setNewBrand(null);
    setNewArticleSlug("");
  };

  const handleRemoveReference = (index) => {
    const updatedReferences = directReferences.filter((_, i) => i !== index);
    // Reorder remaining items
    const reorderedReferences = updatedReferences.map((ref, i) => ({
      ...ref,
      order: i
    }));
    onChange(reorderedReferences);
  };

  const handleReorder = (fromIndex, toIndex) => {
    const updatedReferences = [...directReferences];
    const [movedItem] = updatedReferences.splice(fromIndex, 1);
    updatedReferences.splice(toIndex, 0, movedItem);

    // Update order for all items
    const reorderedReferences = updatedReferences.map((ref, i) => ({
      ...ref,
      order: i
    }));
    onChange(reorderedReferences);
  };

  const formatBrandName = (brand) => {
    if (!brand) { return "Unknown Brand"; }

    const score = brand?.rubricScore?.score;
    const brandName = brand?.Vendor?.vendorName || brand?.name || `Brand ID: ${brand.id}`;

    if (score === undefined || score === null) {
      return brandName;
    } else {
      const stars = Math.round(score);
      const suffix = "★".repeat(stars) + "☆".repeat(5 - stars);
      return `${brandName} (${suffix})`;
    }
  };

  const getReferenceType = (reference) => {
    if (reference.brandId && reference.articleSlug) {
      return "invalid"; // This shouldn't happen with the constraint
    } else if (reference.brandId) {
      return "brand";
    } else if (reference.articleSlug) {
      return "article";
    }
    return "unknown";
  };

  return (
    <Box sx={{ boxShadow: 3, borderRadius: 2, p: 2, bgcolor: "background.paper", mt: 2 }}>
      <Typography variant="h6" gutterBottom>
        Direct References
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Only these results will appear in the carousel. If an article slug is selected, it will only appear on the platform it&apos;s available on (app or webflow).
      </Typography>

      {/* Add new reference form */}
      <Box sx={{ display: "flex", gap: 2, mb: 2 }}>
        <Autocomplete
          sx={{ flex: 1 }}
          options={availableBrands}
          getOptionLabel={(option) => {
            if (!option) { return ""; }
            if (typeof option === "string") { return option; }
            return formatBrandName(option);
          }}
          value={newBrand}
          onChange={(_, newValue) => {
            setNewBrand(newValue);
          }}
          isOptionEqualToValue={(option, value) => {
            if (!option || !value) { return false; }
            return option.id === value.id;
          }}
          renderOption={(props, option) => (
            <li {...props} key={option.id}>
              <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", width: "100%" }}>
                <Box>
                  <Typography variant="body2" fontWeight="medium">
                    {formatBrandName(option)}
                  </Typography>
                </Box>
              </Box>
            </li>
          )}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Select Brand"
              placeholder="Search for a brand..."
              helperText={availableBrands.length === 0 ? "All brands have been selected" : ""}
            />
          )}
          disabled={availableBrands.length === 0}
        />
        <Button
          variant="contained"
          onClick={handleAddReference}
          disabled={!newBrand || availableBrands.length === 0}
          size="small"
        >
          Add Brand
        </Button>
      </Box>

      <Box sx={{ display: "flex", gap: 2, mb: 2 }}>
        <Autocomplete
          sx={{ flex: 1 }}
          options={availableArticleSlugs}
          getOptionLabel={(option) => {
            if (!option) { return ""; }
            if (typeof option === "string") { return option; }
            return option.slug || "";
          }}
          value={newArticleSlug}
          onChange={(_, newValue) => {
            setNewArticleSlug(newValue);
          }}
          isOptionEqualToValue={(option, value) => {
            if (!option || !value) { return false; }
            if (typeof option === "string" && typeof value === "string") { return option === value; }
            if (typeof option === "object" && typeof value === "object") { return option.slug === value.slug; }
            return false;
          }}
          renderOption={(props, option) => (
            <li {...props} key={option.slug || option}>
              <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", width: "100%" }}>
                <Box>
                  <Typography variant="body2">
                    {typeof option === "string" ? option : option.slug}
                  </Typography>
                  {typeof option === "object" && option.title && (
                    <Typography variant="caption" sx={{ ml: 1, color: "text.secondary" }}>
                      {option.title}
                    </Typography>
                  )}
                </Box>
                {typeof option === "object" && option.sources && (
                  <Box>
                    {option.sources.includes("webflow") && (
                      <Chip
                        size="small"
                        label="Webflow"
                        color="primary"
                        variant="outlined"
                        sx={{ ml: 1, height: 20, fontSize: "0.7rem" }}
                      />
                    )}
                    {option.sources.includes("content") && (
                      <Chip
                        size="small"
                        label="App Content"
                        color="secondary"
                        variant="outlined"
                        sx={{ ml: 1, height: 20, fontSize: "0.7rem" }}
                      />
                    )}
                  </Box>
                )}
              </Box>
            </li>
          )}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Select Article Slug"
              placeholder="Search for an article slug..."
              helperText={availableArticleSlugs.length === 0 ? "All article slugs have been selected" : ""}
            />
          )}
          disabled={availableArticleSlugs.length === 0}
        />
        <Button
          variant="contained"
          onClick={handleAddReference}
          disabled={!newArticleSlug || availableArticleSlugs.length === 0}
          size="small"
        >
          Add Article Slug
        </Button>
      </Box>

      {/* Duplicate warning */}
      {duplicateWarning && (
        <Box sx={{ mb: 2 }}>
          <Typography variant="body2" color="error" sx={{ fontStyle: "italic" }}>
            ⚠️ {duplicateWarning}
          </Typography>
        </Box>
      )}
      {/* Article Source Key Legend */}
      <Box
        sx={{
          mb: 2,
          p: 2,
          bgcolor: "background.paper",
          border: "1px solid",
          borderColor: "divider",
          borderRadius: 1
        }}
      >
        <Typography variant="subtitle2" gutterBottom>
          Article Source Key:
        </Typography>
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2, alignItems: "center", mt: 1 }}>
          <Box sx={{ display: "flex", alignItems: "center" }}>
            <Chip
              label="webflow-example"
              size="small"
              color="primary"
              variant="outlined"
              sx={{ mr: 1 }}
            />
            <Typography variant="caption">Webflow</Typography>
          </Box>
          <Box sx={{ display: "flex", alignItems: "center" }}>
            <Chip
              label="content-example"
              size="small"
              color="secondary"
              variant="outlined"
              sx={{ mr: 1 }}
            />
            <Typography variant="caption">App Content</Typography>
          </Box>
          <Box sx={{ display: "flex", alignItems: "center" }}>
            <Chip
              label="both-sources-example"
              size="small"
              color="success"
              variant="outlined"
              sx={{ mr: 1 }}
            />
            <Typography variant="caption">Both Sources</Typography>
          </Box>
          <Box sx={{ display: "flex", alignItems: "center" }}>
            <Chip
              label="custom-entry"
              size="small"
              color="default"
              variant="filled"
              sx={{ mr: 1 }}
            />
            <Typography variant="caption">Custom Entry</Typography>
          </Box>
        </Box>
      </Box>

      {/* Existing references list */}
      {directReferences.length > 0 && (
        <Box>
          <Typography variant="subtitle1" gutterBottom>
            Direct References ({directReferences.length})
          </Typography>
          {directReferences.map((reference, index) => {
            const referenceType = getReferenceType(reference);
            const foundOption = articleSlugOptions.find(o => o.slug === reference.articleSlug);
            const isWebflow = foundOption?.sources?.includes("webflow");
            const isContent = foundOption?.sources?.includes("content");

            return (
              <Card key={reference.id} sx={{ mb: 1 }}>
                <CardContent sx={{ py: 1, "&:last-child": { pb: 1 } }}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <Tooltip title="Drag to reorder">
                      <IconButton
                        size="small"
                        sx={{ cursor: "grab" }}
                        onMouseDown={(e) => {
                          // Simple reordering - move up or down
                          if (index > 0) {
                            handleReorder(index, index - 1);
                          }
                        }}
                      >
                        <DragIndicatorIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>

                    <Box sx={{ flex: 1 }}>
                      {referenceType === "brand"
                        ? (
                          <Typography variant="body2" fontWeight="medium">
                            {reference.Brands ? formatBrandName(reference.Brands) : `Brand ID: ${reference.brandId}`}
                          </Typography>
                        )
                        : referenceType === "article"
                          ? (
                            <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                              <Chip
                                label={reference.articleSlug}
                                size="small"
                                variant={isWebflow || isContent ? "outlined" : "filled"}
                                color={isWebflow && isContent ? "success" : isWebflow ? "primary" : isContent ? "secondary" : "default"}
                              />
                              <Typography variant="caption" color="text.secondary">
                                {foundOption?.title || "Custom slug"}
                              </Typography>
                            </Box>
                          )
                          : (
                            <Typography variant="body2" color="error">
                          Invalid reference (has both brand and article)
                            </Typography>
                          )}

                      <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 0.5 }}>
                        <Chip
                          label={referenceType === "brand" ? "Brand" : "Article"}
                          size="small"
                          variant="outlined"
                        />
                        <Typography variant="caption" color="text.secondary">
                          Order: {reference.order}
                        </Typography>
                        {referenceType === "brand" && reference.Brands?.Vendor?.vendorName && (
                          <Typography variant="caption" color="text.secondary">
                            Vendor: {reference.Brands.Vendor.vendorName}
                          </Typography>
                        )}
                      </Box>
                    </Box>

                    <IconButton
                      size="small"
                      color="error"
                      onClick={() => handleRemoveReference(index)}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Box>
                </CardContent>
              </Card>
            );
          })}
        </Box>
      )}

      {/* Show message when no direct references but other config exists */}
      {directReferences.length === 0 && (
        <Typography variant="body2" color="text.secondary" sx={{ fontStyle: "italic" }}>
          No direct references added yet. Use the form above to add brands or article slugs.
        </Typography>
      )}
    </Box>
  );
};

export default DirectReferencesSection;
